import { TaskInterface } from '@interfaces/Task.interface';
import { TaskProductInterface } from '@interfaces/TaskProduct.interface';
import { DatapointInterface } from '@interfaces/Datapoint.interface';
import { ListDataParams, ListDataResponse } from '@api-client/types';
import BaseApiClient from '@api-client/BaseApiClient';
import type ApiClient from '../ApiClient';
import { isInactive, isDormant } from '../../utils/date';
import { TaskViewUpdateProgressForm } from '@/components/page-components/tasks/TaskView/components/Modals/updateProgress.types';
import type {
  TaskDto,
  TaskUpdateProgressResponse,
  TaskUpdateStatePayload
} from './types';

export type {
  TaskDto,
  TaskUpdateProgressResponse,
  TaskUpdateStatePayload
} from './types';

export class ApiTask extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<TaskInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/task?include=pipelineStage,teamMember', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getDataPoints(): Promise<{
    open_tasks: number;
    completion_rate: number;
    completed_tasks: number;
  }> {
    return new Promise((resolve, reject) => {
      // Match pipelines/data-points: Version selects Flightdeck routes (2025-11-14).
      // CRM-597: endpoint is also registered on unversioned general routes so a
      // missing Version header no longer 405s against PATCH /tasks/{task}.
      this.client
        .get('tasks/data-points', {
          params: { pipeline_type: 'Project' },
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          const body = res.data?.data ?? res.data;
          if (body && typeof body === 'object') {
            resolve(body);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a task
   */
  async get(id: number): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/task/${id}?include=team,teamMember,teamMember.teams,priority,pipelineStage,datapoints,sourceIndividual`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a task with distributions
   */
  async getWithDistributions(id: number): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/task/${id}?include=team,teamMember,teamMember.teams,priority,pipelineStage,datapoints,sourceIndividual,distributions`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get company tasks with active pipelines (or no pipeline) for overview page
   * Fetches tasks with active pipelines (status = 1) OR tasks without pipelines in a single query
   */
  async getCompanyActivePipelineTasks(
    companyUuid: string
  ): Promise<TaskInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/task/company-active-pipeline-tasks?company_uuid=${companyUuid}&include=team,teamMember,teamMember.teams,priority,pipeline,pipelineStage,datapoints,sourceIndividual,paymentFrequency,taskProducts`
        )
        .then((res) => {
          if (res.data && res.data.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete a task
   */
  async delete(id: number) {
    return this.client.delete(`/task/${id}`);
  }

  /**
   * Delete a task collection
   */
  async deleteMultiple(ids: number[]) {
    return this.client.delete('/task', {
      data: { data: ids.map((value: number) => ({ id: value })) }
    });
  }

  /**
   * Store a task
   */
  async store(dto: {
    full_form: boolean | null;
    name: string;
    due_date: string | null;
    source_of_task: string | null;
    pipeline_id: number | null;
    pipeline_stage_id: number | null;
    expected_close_date: string | null;
    confidence_scores: number[] | undefined | null;
    priority_id: number | null;
    next_steps?: string | null;
    next_steps_other?: string | null;
    description: string | null;
    client: Record<string, unknown>;
    team_id: number | null;
    team_member_id: number | null;
    datapoints: DatapointInterface[] | null;
    task_products: TaskProductInterface[] | null;
    is_manual?: boolean;
    type_id?: number | 'other' | null | undefined;
    type_other?: string | null | undefined;
  }): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/task', dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a task
   */
  async update(taskId: number, dto: Partial<TaskDto>): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/task/${taskId}`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a task revenue
   */
  async updateRevenue(
    taskId: number,
    dto: Partial<TaskDto>
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/task/${taskId}/revenue`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  /**
   * TASK PRODUCTS
   */
  /**
   * Get task products
   */
  async getTaskProducts(taskId: number): Promise<TaskProductInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/task/${taskId}/task-product`)
        .then((res) => {
          if (res.data) {
            resolve(res.data?.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  /**
   * Get task product
   */
  async getTaskProduct(
    taskId: number,
    taskProductId: number
  ): Promise<TaskProductInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/task/${taskId}/task-product/${taskProductId}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Store task product
   */
  async storeTaskProduct(
    taskId: number,
    dto: TaskProductInterface
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/task/${taskId}/task-product`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update task product
   */
  async updateTaskProduct(
    taskId: number,
    taskProductId: number,
    dto: Partial<TaskProductInterface>
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/task/${taskId}/task-product/${taskProductId}`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete task product
   */
  async deleteTaskProduct(taskId: number, taskProductId: number) {
    return this.client.delete(`/task/${taskId}/task-product/${taskProductId}`);
  }

  /**
   * Send email
   */
  async sendEmail(
    id: number,
    dto: {
      email: string;
      emailMessage: string;
      emailSubject: string;
      cc?: string;
      bcc?: string;
      from_type?: 'tenant_noreply' | 'user';
      emailAttachments?: {
        id: number;
        file_name: string;
        file_path: string;
        file_type: string;
      }[];
    }
  ) {
    return this.client.post(`/task/${id}/send-email`, dto);
  }

  async getOpenTasks(): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/task?filter[open]=true')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update task progress
   *   taskId: string | number, 
  dto: { 
    note_title: string; 
    note_text: string; 
  }
   */
  async reactivateTask(
    taskId: string | number,
    dto: {
      entity_uuid?: string | null;
      note_title: string;
      note_text: string;
      pipeline_stage_id?: number | null;
    }
  ): Promise<TaskUpdateProgressResponse> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/tasks/${taskId}/notes/progress`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateProgress(
    taskId: string | number,
    dto: TaskViewUpdateProgressForm
  ): Promise<TaskUpdateProgressResponse> {
    const formData = new FormData();
    formData.append('pipeline_id', dto.pipeline_id?.toString() || '');
    formData.append(
      'pipeline_stage_id',
      dto.pipeline_stage_id?.toString() || ''
    );

    if (dto.document) {
      formData.append('document', dto.document);
    }

    formData.append('_method', 'PATCH');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/tasks/${taskId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateState(
    taskId: string | number,
    dto: TaskUpdateStatePayload
  ): Promise<TaskUpdateProgressResponse> {
    const formData = new FormData();
    formData.append('state', dto.state);

    if (dto.lost_reason_id != null) {
      formData.append('lost_reason_id', dto.lost_reason_id.toString());
    }

    if (dto.document) {
      formData.append('document', dto.document);
    }

    formData.append('_method', 'PATCH');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/tasks/${taskId}/state`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  // Version 2025-11-14
  async createTask(
    pipelineStageId: number,
    payload: any
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/pipeline-stages/${pipelineStageId}/tasks`, payload, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateTask(
    pipelineStageId: number,
    taskId: number,
    is_opportunity: boolean,
    payload: any
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(
          `/pipeline-stages/${pipelineStageId}/tasks/${taskId}`,
          { ...payload, is_opportunity },
          {
            headers: {
              Version: '2025-11-14'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async createOpportunity(payload: any): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/opportunities', payload, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async updateOpportunity(
    opportunityId: number,
    payload: any
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/opportunities/${opportunityId}`, payload, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateTaskCustomFields(
    pipelineStageId: number,
    taskId: number,
    payload: {
      custom_fields: {
        field_id: number;
        value: string | number | Array<string | number> | null;
      }[];
    }
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(
          `/pipeline-stages/${pipelineStageId}/tasks/${taskId}/custom-fields`,
          payload,
          {
            headers: {
              Version: '2025-11-14'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get professions list
   */
  async getProfessions(
    filter?: string
  ): Promise<Array<{ id: number; name: string }>> {
    return new Promise((resolve, reject) => {
      let url = '/professions?page[size]=999';
      if (filter) {
        url += `&filter[name]=${encodeURIComponent(filter)}`;
      }
      this.client
        .get(url, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data && res.data.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get age groups list
   */
  async getAgeGroups(): Promise<Array<{ id: number; name: string }>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/age-groups?page[size]=999', {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data && res.data.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get annual incomes list
   */
  async getAnnualIncomes(): Promise<Array<{ id: number; name: string }>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/annual-incomes?page[size]=999', {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data && res.data.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create a distribution for a task
   */
  async createDistribution(
    taskId: number,
    dto: {
      brand: string;
      channel: string;
      intermediary: boolean;
      intermediary_brand?: string;
    }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/tasks/${taskId}/distributions`, dto, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update distributions for a task (PUT endpoint)
   */
  async updateDistributions(
    taskId: number,
    dto: {
      data: Array<{
        brand: string;
        channel_id: number;
        is_intermediary: boolean;
        intermediary_brand?: string;
      }>;
    }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/tasks/${taskId}/distributions`, dto, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete a distribution
   */
  async deleteDistribution(distributionId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/distributions/${distributionId}`, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then(() => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Sync task audiences
   */
  async syncTaskAudiences(
    taskId: number,
    dto: {
      age_groups?: Array<{ id: number }>;
      professions?: Array<{ id: number }>;
      annual_incomes?: Array<{ id: number }>;
      locations?: Array<{ value: string }>;
    }
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/tasks/${taskId}/audiences`, dto, {
          headers: {
            Version: '2025-11-14'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update task linked contact
   */
  async updateTaskLinkedContact(
    pipelineStageId: number,
    taskId: number,
    payload: {
      client: Record<string, unknown>;
    }
  ): Promise<TaskInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(
          `/pipeline-stages/${pipelineStageId}/tasks/${taskId}/linked-contact`,
          payload,
          {
            headers: {
              Version: '2025-11-14'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { taskQueryKeys } from './query-keys';
export type { TaskListQueryParams, TaskProfessionsQueryParams } from './query-keys';
export {
  useTaskAgeGroupsQuery,
  useTaskAnnualIncomesQuery,
  useTaskCompanyActivePipelineTasksQuery,
  useTaskListQuery,
  useTaskOpenTasksQuery,
  useTaskProductQuery,
  useTaskProductsQuery,
  useTaskProfessionsQuery,
  useTaskQuery,
  useTaskWithDistributionsQuery
} from './use-task-queries';
export {
  useTaskCreateDistributionMutation,
  useTaskCreateOpportunityMutation,
  useTaskCreateTaskMutation,
  useTaskDeleteDistributionMutation,
  useTaskDeleteMultipleMutation,
  useTaskDeleteMutation,
  useTaskDeleteTaskProductMutation,
  useTaskReactivateMutation,
  useTaskSendEmailMutation,
  useTaskStoreMutation,
  useTaskStoreTaskProductMutation,
  useTaskSyncTaskAudiencesMutation,
  useTaskUpdateDistributionsMutation,
  useTaskUpdateMutation,
  useTaskUpdateOpportunityMutation,
  useTaskUpdateProgressMutation,
  useTaskUpdateStateMutation,
  useTaskUpdateRevenueMutation,
  useTaskUpdateTaskCustomFieldsMutation,
  useTaskUpdateTaskLinkedContactMutation,
  useTaskUpdateTaskMutation,
  useTaskUpdateTaskProductMutation
} from './use-task-mutations';
export type {
  TaskCreateDistributionVariables,
  TaskCreateTaskVariables,
  TaskDeleteTaskProductVariables,
  TaskReactivateVariables,
  TaskSendEmailVariables,
  TaskStoreTaskProductVariables,
  TaskStoreVariables,
  TaskSyncTaskAudiencesVariables,
  TaskUpdateDistributionsVariables,
  TaskUpdateOpportunityVariables,
  TaskUpdateProgressVariables,
  TaskUpdateStateVariables,
  TaskUpdateTaskCustomFieldsVariables,
  TaskUpdateTaskLinkedContactVariables,
  TaskUpdateTaskProductVariables,
  TaskUpdateTaskVariables,
  TaskUpdateVariables
} from './use-task-mutations';
